import * as i0 from '@angular/core';
import { Injectable, Inject, NgModule } from '@angular/core';
import { get } from 'lodash-es';
import { DateTime } from 'luxon';
import * as i1 from '@upr-web-primer/dal';
import { DalModule } from '@upr-web-primer/dal';
import * as i2 from '@upr-web-primer/locale';
import { LocaleModule } from '@upr-web-primer/locale';
import { HttpHeaders } from '@angular/common/http';

const PlacesUrls = {
    GET_PLACE: '/resort-areas/{resortAreaCode}/places/{placeId}',
    SEARCH_PLACES: '/resort-areas/{resortAreaCode}/search-places',
};

class MsPlacesCommonUtilityService {
    constructor(appDefaults) {
        this.appDefaults = appDefaults;
        this.timeFormats = ['hh:mm a', 'HH:mm'];
        this.timeZone = this.appDefaults.UPR_TIME_ZONE;
    }
    getTodaysHours(place) {
        const periods = get(place, 'place_hours.periods');
        if (!periods || !Array.isArray(periods)) {
            return;
        }
        const currentDay = this.getCurrentWeekDay();
        for (const period of periods) {
            const { open, close } = period;
            const openDay = open.day === 0 ? 7 : open.day;
            if (currentDay === openDay) {
                return this.getHoursFromFormats(open, close);
            }
        }
    }
    getWeekHours(place, transform) {
        const periods = get(place, 'place_hours.periods');
        if (!periods || !Array.isArray(periods)) {
            return;
        }
        const mappedHours = periods
            .slice(0, 7)
            .reduce((mappedPeriods, { open, close }) => {
            const hours = this.getHoursFromFormats(open, close);
            if (!hours) {
                return mappedPeriods;
            }
            if (transform) {
                mappedPeriods.set(hours.open, transform(hours));
            }
            else {
                mappedPeriods.set(hours.open, hours);
            }
            return mappedPeriods;
        }, new Map());
        return Array.from(mappedHours.keys())
            .sort((hour1, hour2) => {
            const diff = hour1.toUnixInteger() - hour2.toUnixInteger();
            if (diff < 0) {
                return -1;
            }
            else if (diff > 0) {
                return 1;
            }
            return 0;
        })
            .map((date) => mappedHours.get(date));
    }
    getHoursFromFormats(open, close) {
        const currentDay = this.getCurrentWeekDay();
        const openDay = open.day === 0 ? 7 : open.day;
        const closeDay = close.day === 0 ? 7 : close.day;
        let openDayDiff = openDay - currentDay;
        let closeDayDiff = closeDay - currentDay;
        let openDateTime;
        let closeDateTime;
        if (openDayDiff < 0) {
            openDayDiff += 7;
        }
        if (closeDayDiff < 0) {
            closeDayDiff += 7;
        }
        for (const format of this.timeFormats) {
            const openTime = DateTime.fromFormat(open.time, format, {
                zone: this.timeZone,
            });
            const closeTime = DateTime.fromFormat(close.time, format, {
                zone: this.timeZone,
            });
            if (openTime.isValid && closeTime.isValid) {
                openDateTime =
                    openDayDiff === 0
                        ? openTime
                        : openTime.plus({ days: 7 - openDayDiff });
                closeDateTime =
                    closeDayDiff === 0
                        ? closeTime
                        : closeTime.plus({ days: 7 - closeDayDiff });
                break;
            }
        }
        if (!openDateTime || !closeDateTime) {
            return;
        }
        return {
            open: openDateTime,
            close: closeDateTime,
        };
    }
    getCurrentWeekDay() {
        return DateTime.now().setZone(this.timeZone).weekday;
    }
}
MsPlacesCommonUtilityService.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "13.3.11", ngImport: i0, type: MsPlacesCommonUtilityService, deps: [{ token: 'AppDefaults' }], target: i0.ɵɵFactoryTarget.Injectable });
MsPlacesCommonUtilityService.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "13.3.11", ngImport: i0, type: MsPlacesCommonUtilityService, providedIn: 'root' });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "13.3.11", ngImport: i0, type: MsPlacesCommonUtilityService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root',
                }]
        }], ctorParameters: function () { return [{ type: undefined, decorators: [{
                    type: Inject,
                    args: ['AppDefaults']
                }] }]; } });

class MsPlacesModule {
}
MsPlacesModule.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "13.3.11", ngImport: i0, type: MsPlacesModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule });
MsPlacesModule.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "12.0.0", version: "13.3.11", ngImport: i0, type: MsPlacesModule, imports: [DalModule, LocaleModule] });
MsPlacesModule.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "13.3.11", ngImport: i0, type: MsPlacesModule, imports: [[DalModule, LocaleModule]] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "13.3.11", ngImport: i0, type: MsPlacesModule, decorators: [{
            type: NgModule,
            args: [{
                    imports: [DalModule, LocaleModule],
                }]
        }] });

class MsPlacesService {
    constructor(dal, localeService, appDefaults) {
        this.dal = dal;
        this.localeService = localeService;
        this.appDefaults = appDefaults;
    }
    getPlaceById(id) {
        const relativeUrl = this.replaceResortAreaCode(this.replacePlaceId(PlacesUrls.GET_PLACE, id));
        const httpOptions = this.createHeaders();
        return this.dal.httpRequest(relativeUrl, 'GET', {}, httpOptions);
    }
    searchPlaces(body) {
        const relativeUrl = this.replaceResortAreaCode(PlacesUrls.SEARCH_PLACES);
        const httpOptions = this.createHeaders();
        return this.dal.httpRequest(relativeUrl, 'POST', body, httpOptions);
    }
    createHeaders() {
        const locale = this.localeService.getFromStorage();
        const language = locale?.language;
        const country = locale?.country?.toUpperCase();
        return new HttpHeaders({
            'Accept-Language': `${language}-${country}` + (language === 'es' ? ',en-US' : ''),
        });
    }
    replaceResortAreaCode(url) {
        return url
            ? url.replace('{resortAreaCode}', this.appDefaults.PERSONALIZE_RESORT_CODE)
            : '';
    }
    replacePlaceId(url, id) {
        return url ? url.replace('{placeId}', id) : '';
    }
}
MsPlacesService.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "13.3.11", ngImport: i0, type: MsPlacesService, deps: [{ token: i1.DalService }, { token: i2.LocaleService }, { token: 'AppDefaults' }], target: i0.ɵɵFactoryTarget.Injectable });
MsPlacesService.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "13.3.11", ngImport: i0, type: MsPlacesService, providedIn: 'root' });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "13.3.11", ngImport: i0, type: MsPlacesService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root',
                }]
        }], ctorParameters: function () { return [{ type: i1.DalService }, { type: i2.LocaleService }, { type: undefined, decorators: [{
                    type: Inject,
                    args: ['AppDefaults']
                }] }]; } });

export { MsPlacesCommonUtilityService, MsPlacesModule, MsPlacesService, PlacesUrls };
